import { useStore, selectLineage, selectCreature, selectNpcs } from '@/infra/store';
import type { LineageGraph, LineageNode } from '@/domain/lineage/types';
import { useT } from '@/infra/i18n';
import { COLORS, FONT, SPACING } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';

const ASSET_ROOT = '/assets/generated/house';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function LineageModal({ open, onClose }: Props) {
  const t = useT();
  const lineage = useStore(selectLineage);
  const active = useStore(selectCreature);
  const npcs = useStore(selectNpcs);
  if (!open) return null;

  const totalNodes = Object.keys(lineage.nodes).length;
  const hasAncestors = active
    ? lineage.nodes[active.id]?.parentIds.some((id) => id !== null && lineage.nodes[id] !== undefined) ?? false
    : false;
  const aliveNpcs = npcs.filter((n) => !n.dead);
  const deadNpcs = npcs.filter((n) => n.dead);
  const activeParentIds = new Set(
    active ? lineage.nodes[active.id]?.parentIds.filter((id): id is string => id !== null) ?? [] : [],
  );

  return (
    <div role="dialog" data-testid="lineage-modal" style={overlayStyle} onClick={onClose}>
      <div style={{ ...cardStyle, ...lineageCard }} onClick={(e) => e.stopPropagation()}>
        <h2 style={title}>
          <img src={`${ASSET_ROOT}/icon-lineage.png`} alt="" draggable={false} style={titleIcon} />
          {t('lineage.title')}
        </h2>
        <p style={subtitle}>{t('lineage.totalRegistered', { n: String(totalNodes) })}</p>

        {totalNodes === 0 ? (
          <p style={empty}>{t('lineage.empty')}</p>
        ) : (
          <>
            {active && (
              <Section label={t('lineage.activeLabel')}>
                <Item color={COLORS.accent}>
                  <strong>{active.name}</strong> - {t(`stage.${active.lifecycle.stage}`)}
                </Item>
              </Section>
            )}
            {active && hasAncestors && (
              <Section label={t('lineage.ancestors')}>
                <FamilyTree lineage={lineage} rootId={active.id} />
              </Section>
            )}
            {aliveNpcs.length > 0 && (
              <Section label={t('lineage.npcsAlive', { n: String(aliveNpcs.length) })}>
                {aliveNpcs.slice(0, 10).map((n) => (
                  <Item key={n.id} color={COLORS.text} testId={`lineage-npc-${n.id}`}>
                    {n.name} - {t(`stage.${n.stage}`)}
                    {activeParentIds.has(n.id) ? ` · ${t('lineage.family')}` : ''}
                    {n.careFinalScore !== null
                      ? ` · ${t('lineage.careLegacy', { score: String(Math.round(n.careFinalScore)) })}`
                      : ''}
                  </Item>
                ))}
              </Section>
            )}
            {deadNpcs.length > 0 && (
              <Section label={t('lineage.memorials', { n: String(deadNpcs.length) })}>
                {deadNpcs.slice(0, 10).map((n) => (
                  <Item key={n.id} color={COLORS.textFaint}>
                    <span style={inlineItem}>
                      <img src={`${ASSET_ROOT}/icon-lineage.png`} alt="" draggable={false} style={inlineIcon} />
                      {n.name} ({t(`lineage.cause.${n.causeOfDeath ?? 'old-age'}`)})
                    </span>
                  </Item>
                ))}
              </Section>
            )}
          </>
        )}

        <div style={{ textAlign: 'right', marginTop: SPACING.md }}>
          <button data-testid="lineage-close" onClick={onClose} style={primaryBtn}>
            {t('lineage.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: SPACING.md }}>
      <h3 style={sectionTitle}>{label}</h3>
      {children}
    </div>
  );
}

function Item({ children, color, testId }: { children: React.ReactNode; color: string; testId?: string }) {
  return <p data-testid={testId} style={{ margin: '4px 0', color, fontSize: FONT.sizeSm }}>{children}</p>;
}

interface TreeNodePosition {
  node: LineageNode;
  x: number;
  y: number;
  role: 'active' | 'parent' | 'grandparent';
}

interface TreeEdge {
  childId: string;
  parentId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

function familyTreeLayout(lineage: LineageGraph, rootId: string): { nodes: TreeNodePosition[]; edges: TreeEdge[] } {
  const root = lineage.nodes[rootId];
  if (!root) return { nodes: [], edges: [] };
  const nodes: TreeNodePosition[] = [{ node: root, x: 360, y: 14, role: 'active' }];
  const edges: TreeEdge[] = [];
  const parentXs = [180, 540];
  const grandparentXs = [90, 270, 450, 630];

  root.parentIds.forEach((parentId, parentIndex) => {
    if (!parentId) return;
    const parent = lineage.nodes[parentId];
    if (!parent) return;
    const parentX = parentXs[parentIndex]!;
    nodes.push({ node: parent, x: parentX, y: 112, role: 'parent' });
    edges.push({ childId: rootId, parentId, fromX: 360, fromY: 64, toX: parentX, toY: 112 });

    parent.parentIds.forEach((grandparentId, grandparentIndex) => {
      if (!grandparentId) return;
      const grandparent = lineage.nodes[grandparentId];
      if (!grandparent) return;
      const grandparentX = grandparentXs[parentIndex * 2 + grandparentIndex]!;
      nodes.push({ node: grandparent, x: grandparentX, y: 210, role: 'grandparent' });
      edges.push({ childId: parentId, parentId: grandparentId, fromX: parentX, fromY: 162, toX: grandparentX, toY: 210 });
    });
  });
  return { nodes, edges };
}

function FamilyTree({ lineage, rootId }: { lineage: LineageGraph; rootId: string }) {
  const t = useT();
  const layout = familyTreeLayout(lineage, rootId);
  const w = 720;
  const h = 276;
  return (
    <div style={treeScroller} data-testid="lineage-family-tree">
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-label={t('lineage.treeLabel')}>
        {layout.edges.map((edge) => {
          const middleY = (edge.fromY + edge.toY) / 2;
          return (
            <path
              key={`${edge.childId}-${edge.parentId}`}
              data-testid={`lineage-tree-edge-${edge.childId}-${edge.parentId}`}
              d={`M ${edge.fromX} ${edge.fromY} V ${middleY} H ${edge.toX} V ${edge.toY}`}
              fill="none"
              stroke={COLORS.border}
              strokeWidth={2}
            />
          );
        })}
        {layout.nodes.map(({ node, x, y, role }) => {
          const isActive = role === 'active';
          const width = role === 'grandparent' ? 138 : 156;
          const status = t(`lineage.cause.${node.cause}`);
          return (
            <g key={node.creatureId} data-testid={`lineage-tree-node-${node.creatureId}`}>
              <title>{`${node.name} · ${t(`lineage.role.${role}`)} · ${status}`}</title>
              <rect
                x={x - width / 2} y={y} width={width} height={50} rx={8}
                fill={isActive ? COLORS.accent : COLORS.surfaceAlt}
                stroke={isActive ? COLORS.accent : COLORS.border}
                strokeWidth={2}
              />
              <text x={x} y={y + 17} textAnchor="middle" fontSize={9} fill={isActive ? COLORS.bg : COLORS.textFaint}>
                {t(`lineage.role.${role}`).toUpperCase()}
              </text>
              <text x={x} y={y + 34} textAnchor="middle" fontSize={11} fontWeight={700} fill={isActive ? COLORS.bg : COLORS.text}>
                {compactName(node.name, role === 'grandparent' ? 16 : 20)}
              </text>
              <circle cx={x - width / 2 + 10} cy={y + 10} r={3} fill={node.cause === 'alive' ? '#86ef9b' : COLORS.textFaint} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function compactName(name: string, maxLength: number): string {
  return name.length <= maxLength ? name : `${name.slice(0, maxLength - 1)}…`;
}

const title: React.CSSProperties = {
  margin: 0,
  color: COLORS.accent,
  fontSize: FONT.sizeXl,
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.xs,
};
const lineageCard: React.CSSProperties = {
  maxWidth: 780,
};
const treeScroller: React.CSSProperties = {
  width: '100%',
  overflowX: 'auto',
  paddingBottom: 4,
};
const titleIcon: React.CSSProperties = {
  width: 36,
  height: 36,
  objectFit: 'contain',
  imageRendering: 'auto',
};
const subtitle: React.CSSProperties = {
  margin: `${SPACING.xs}px 0 ${SPACING.lg}px 0`,
  color: COLORS.textDim,
  fontSize: FONT.sizeSm,
};
const empty: React.CSSProperties = {
  color: COLORS.textFaint,
  fontStyle: 'italic',
  fontSize: FONT.sizeSm,
  margin: SPACING.md,
};
const sectionTitle: React.CSSProperties = {
  margin: `0 0 ${SPACING.sm}px 0`,
  color: COLORS.textDim,
  fontSize: FONT.sizeSm,
  borderBottom: `1px solid ${COLORS.border}`,
  paddingBottom: 4,
};
const inlineItem: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
const inlineIcon: React.CSSProperties = {
  width: 20,
  height: 20,
  objectFit: 'contain',
  imageRendering: 'auto',
};
const primaryBtn: React.CSSProperties = {
  background: COLORS.accent,
  color: COLORS.bg,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontWeight: 600,
};
